import { useEffect, useRef, useState } from "react";
import { Flex, Grid, Typography } from "antd";
import styled from "styled-components";
import type { PdfViewerHandle } from "@libertify/experience-viewer";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import {
  AlertCard,
  Button,
  IconButton,
  type StandoutNotation,
} from "@libertify/ui";
import { useNotification, useTranslate } from "@refinedev/core";
import { useAssetListAutosave } from "../hooks/useAssetListAutosave";
import { StandoutCreatePopover } from "./StandoutCreatePopover";
import { StandoutEditItem } from "./StandoutEditItem";
import { StandoutStylePopover } from "./StandoutStylePopover";
import { StandoutsDocumentPane } from "./StandoutsDocumentPane";
import {
  changeNotation,
  defaultColor,
  isSameWire,
  newStandoutRow,
  resolveRow,
  rowRoughNotation,
  toRows,
  toWire,
  withPassage,
} from "./helpers";
import { useSaveStandouts } from "./useSaveStandouts";
import { useStandoutHighlight } from "./useStandoutHighlight";
import { useStandoutSelection } from "./useStandoutSelection";
import type { ProjectDocument, Standout, StandoutRow } from "./types";
import { KEY } from "./constants";

export type StandoutsFormProps = {
  loaded: Standout[];
  assetId: string;
  projectId: string;
  projectDocuments: ProjectDocument[];
};

/** Mounted once the standouts are in, and seeded once; owns the autosave. */
export const StandoutsForm = ({
  loaded,
  assetId,
  projectId,
  projectDocuments,
}: StandoutsFormProps) => {
  const t = useTranslate();
  const { open } = useNotification();
  const { save } = useSaveStandouts(projectId);
  const viewerRef = useRef<PdfViewerHandle>(null);
  const highlight = useStandoutHighlight(projectDocuments, viewerRef);
  const selection = useStandoutSelection(highlight.activeDocumentId);
  // Below AntD's `xl` the panes stack; their own styles switch at the same width.
  const stacked = !Grid.useBreakpoint().xl;

  const [initialRows] = useState(() => toRows(loaded));
  const [rows, setRows] = useState<StandoutRow[]>(initialRows);
  // One style popover at a time: opening a row's closes the last one.
  const [styleRowId, setStyleRowId] = useState<string | null>(null);
  const listPane = useRef<HTMLDivElement>(null);
  // Set when a standout is created; the list scrolls once it has rendered.
  const scrollToNewRow = useRef(false);
  // The row whose passage the next selection replaces. Derived, so deleting
  // that row ends the mode.
  const [reselectRowId, setReselectRowId] = useState<string | null>(null);
  const reselectRow = rows.find((row) => row.id === reselectRowId);

  useEffect(() => {
    if (!scrollToNewRow.current) return;
    scrollToNewRow.current = false;
    // New rows go last. Side by side only the pane scrolls, never the page
    // around it; stacked, the pane doesn't scroll, so the page has to.
    const pane = listPane.current;
    if (stacked) {
      pane?.lastElementChild?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    } else {
      pane?.scrollTo({ top: pane.scrollHeight, behavior: "smooth" });
    }
  }, [rows, stacked]);

  const persist = useAssetListAutosave<StandoutRow, Standout>({
    initialRows,
    saveKey: assetId,
    resolveRow,
    toWire,
    isSameWire,
    save: (standouts) => save(assetId, standouts),
    onSaveFailed: () =>
      open?.({ type: "error", message: t(`${KEY}.saveFailed`) }),
    revert: setRows,
  });

  const update = (next: StandoutRow[]) => {
    setRows(next);
    persist(next);
  };

  const updateRow = (id: string, patch: Partial<StandoutRow>) =>
    update(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  /** The picked marker over the selected text: added last, and shown. */
  const createFromSelection = (notation: StandoutNotation) => {
    const { pending } = selection;
    if (!pending) return;
    const row = newStandoutRow(pending.zone, pending.documentId, notation);
    scrollToNewRow.current = true;
    update([...rows, row]);
    selection.dismiss();
    highlight.show(row);
  };

  const startReselect = (row: StandoutRow) => {
    selection.dismiss();
    setReselectRowId(row.id);
    highlight.show(row);
  };

  const cancelReselect = () => {
    selection.dismiss();
    setReselectRowId(null);
  };

  /** The picked marker over the selected text, moving the reselected row. */
  const reselectFromSelection = (notation: StandoutNotation) => {
    const { pending } = selection;
    if (!pending || !reselectRow) return;
    const moved = withPassage(
      reselectRow,
      pending.zone,
      pending.documentId,
      notation
    );
    // A new type resets padding, as picking it on the card does.
    const row =
      notation === reselectRow.notation
        ? moved
        : changeNotation(moved, notation);
    update(rows.map((r) => (r.id === row.id ? row : r)));
    selection.dismiss();
    setReselectRowId(null);
    highlight.show(row);
  };

  const reselecting = Boolean(reselectRow);
  useEffect(() => {
    if (!reselecting) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setReselectRowId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [reselecting]);

  return (
    <Flex
      gap={20}
      vertical={stacked}
      align={stacked ? "stretch" : "flex-start"}
    >
      <StyledDocumentPane vertical gap={12}>
        {reselectRow ? (
          <AlertCard
            type="info"
            title={t(`${KEY}.reselect.banner`)}
            action={
              <Button variant="tertiary" size="s" onClick={cancelReselect}>
                {t(`${KEY}.reselect.cancel`)}
              </Button>
            }
          />
        ) : null}
        <StandoutsDocumentPane
          highlight={highlight}
          viewerRef={viewerRef}
          projectId={projectId}
          onTextSelection={selection.onTextSelection}
        />
      </StyledDocumentPane>
      <StyledListPane ref={listPane} vertical gap={10}>
        <Typography.Text type="secondary">
          {t(`${KEY}.createHint`)}
        </Typography.Text>
        {/* The document stays up when the list empties: it's where the next
            standout gets created. */}
        {rows.length === 0 ? (
          <Typography.Text type="secondary">
            {t(`${KEY}.noStandouts`)}
          </Typography.Text>
        ) : null}
        {rows.map((row, index) => {
          const mark = rowRoughNotation(row);
          return (
            <StandoutEditItem
              key={row.id}
              index={index}
              title={row.title}
              notation={row.notation}
              color={mark.color}
              active={row.id === highlight.activeRowId}
              styleControl={
                <StandoutStylePopover
                  open={row.id === styleRowId}
                  onOpenChange={(open) => {
                    setStyleRowId(open ? row.id : null);
                    // Styling a standout shows it, without replaying a shown one.
                    if (open && row.id !== highlight.activeRowId)
                      highlight.show(row);
                  }}
                  notation={row.notation}
                  mark={mark}
                  defaultColor={defaultColor(row)}
                  onChange={(patch) => {
                    const next = { ...row, style: { ...row.style, ...patch } };
                    updateRow(row.id, { style: next.style });
                    // Only the shown mark redraws; another row's applies when shown.
                    if (row.id === highlight.activeRowId) highlight.show(next);
                  }}
                >
                  <IconButton
                    variant="tertiary"
                    size="s"
                    iconSize={14}
                    icon={<PencilSimpleIcon size={14} />}
                    aria-label={t(`${KEY}.style.button`, { number: index + 1 })}
                    aria-expanded={row.id === styleRowId}
                  />
                </StandoutStylePopover>
              }
              onShow={() => highlight.show(row)}
              onTitleChange={(title) => updateRow(row.id, { title })}
              // Editing a title shows its standout, without replaying it per focus.
              onTitleFocus={() => {
                if (row.id !== highlight.activeRowId) highlight.show(row);
              }}
              onNotationChange={(notation) => {
                const next = changeNotation(row, notation);
                updateRow(row.id, { notation, style: next.style });
                // Changing a type previews it: the row becomes the shown one.
                highlight.show(next);
              }}
              onReselect={() => startReselect(row)}
              onRemove={() => {
                if (row.id === highlight.activeRowId) highlight.clear();
                update(rows.filter(({ id }) => id !== row.id));
              }}
            />
          );
        })}
      </StyledListPane>
      {selection.pending ? (
        <StandoutCreatePopover
          title={t(
            reselectRow ? `${KEY}.reselect.title` : `${KEY}.create.title`
          )}
          anchor={selection.pending.anchor}
          onPick={reselectRow ? reselectFromSelection : createFromSelection}
          onDismiss={selection.dismiss}
          onClose={selection.close}
        />
      ) : null}
    </Flex>
  );
};

export default StandoutsForm;

// ─── Styled components ───

/**
 * The mock's ~65% document pane. The PDF scrolls inside itself, so the pane
 * takes one screen's height.
 */
const StyledDocumentPane = styled(Flex)`
  flex: 1 1 65%;
  min-width: 0;
  position: sticky;
  top: 24px;
  height: calc(100vh - 48px);
  min-height: 480px;

  @media (width < 1200px) {
    position: static;

    /* Stacked, a 65% basis would override the height and leave the PDF 0px tall. */
    flex: none;
    height: 70vh;
  }
`;

/** The mock's ~35% list, sticky beside the document. */
const StyledListPane = styled(Flex)`
  flex: 1 1 35%;
  min-width: 260px;
  position: sticky;
  top: 24px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding-right: 4px;

  @media (width < 1200px) {
    position: static;
    min-width: 0;
    max-height: none;
    overflow-y: visible;
    padding-right: 0;
  }
`;
